/**
 * useDrawInitialization — React Query hooks for the ferris-wheel
 * initialization workflow.
 *
 * Wraps the /draw-ops endpoints added for sandbox→live transition:
 *   GET    /draw-ops/rodeo/:rodeoId/draw-mode                 — status
 *   POST   /draw-ops/rodeo/:rodeoId/initialize                — execute rotation
 *   GET    /draw-ops/rodeo/:rodeoId/initialization-snapshot   — per-rodeo detail
 *   GET    /draw-ops/association/:assocId/initialization-history — list
 *
 * Used by the sandbox-mode banner + Initialize Draw confirmation flow on
 * the delegate's RodeoDrawManagement page, and by the per-rodeo
 * snapshot-history view admins/delegates open to audit prior→new positions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';

export const drawInitializationKeys = {
  all: ['draw-initialization'] as const,
  status: (rodeoId: string) =>
    [...drawInitializationKeys.all, 'status', rodeoId] as const,
  snapshot: (rodeoId: string) =>
    [...drawInitializationKeys.all, 'snapshot', rodeoId] as const,
  history: (associationId: string) =>
    [...drawInitializationKeys.all, 'history', associationId] as const,
};

export interface DrawInitializationSnapshotSummary {
  id: string;
  rodeo_id: string;
  association_id: string;
  executed_by: string | null;
  executed_at: string;
  random_seed: number;
  member_count: number;
  group_count: number;
  group_sizes: number[];
  notes: string | null;
}

export interface DrawInitializationPositionChange {
  id: string;
  user_profile_id: string;
  association_priority_id: string | null;
  card_number: string | null;
  prior_position: number | null;
  prior_group: number | null;
  new_position: number;
  new_group: number;
  /**
   * Display name resolved server-side from user_profiles. Falls back
   * to legal name when display name isn't set; null only when the
   * profile is missing or both name pairs are empty.
   */
  display_name?: string | null;
}

export interface DrawInitializationSnapshotDetail
  extends DrawInitializationSnapshotSummary {
  changes: DrawInitializationPositionChange[];
}

export interface DrawModeStatus {
  rodeo_id: string;
  draw_mode: 'sandbox' | 'live';
  snapshot: DrawInitializationSnapshotSummary | null;
  success: boolean;
}

export interface InitializeDrawResult {
  snapshot_id: string;
  rodeo_id: string;
  association_id: string;
  member_count: number;
  group_sizes: [number, number, number, number];
  random_seed: number;
  draw_mode: 'live';
  members: DrawInitializationPositionChange[];
  /** Number of sandbox-era draft draws that were wiped during init. */
  draft_draws_reset: number;
  success: boolean;
}

/**
 * Fetch a rodeo's draw mode (sandbox vs live) plus, if initialized,
 * the snapshot summary. UI uses this to decide whether to render the
 * "Initialize Draw" CTA or the "view history" affordance.
 */
export function useDrawMode(rodeoId: string | null) {
  return useQuery<DrawModeStatus>({
    queryKey: drawInitializationKeys.status(rodeoId ?? ''),
    queryFn: () =>
      httpClient.get<DrawModeStatus>(`/draw-ops/rodeo/${rodeoId}/draw-mode`),
    enabled: !!rodeoId,
  });
}

/**
 * Mutation: kick off the one-time ferris-wheel initialization and
 * flip draw_mode to 'live'. Backend rejects with 409/ALREADY_LIVE if
 * the rodeo is already live. An association with no memberships is a
 * valid case — it initializes with member_count 0 and an empty rotation.
 */
export function useInitializeDraw() {
  const qc = useQueryClient();
  return useMutation<
    InitializeDrawResult,
    Error & { code?: string; status?: number; body?: unknown },
    { rodeoId: string; seed?: number; notes?: string }
  >({
    mutationFn: async ({ rodeoId, seed, notes }) => {
      const body: { seed?: number; notes?: string } = {};
      if (typeof seed === 'number') body.seed = seed;
      if (typeof notes === 'string' && notes.trim().length > 0)
        body.notes = notes;
      // ALWAYS send the body (even if empty) — Fastify validates the
      // schema (`Type.Optional(Type.Object(...))`) regardless of whether
      // a body was provided, and a missing body fails the type:'object'
      // check with "body must be object". Sending {} satisfies the
      // schema while still letting the backend treat both keys as
      // optional.
      return httpClient.post<InitializeDrawResult>(
        `/draw-ops/rodeo/${rodeoId}/initialize`,
        body,
      );
    },
    onSuccess: (_data, { rodeoId }) => {
      qc.invalidateQueries({ queryKey: drawInitializationKeys.status(rodeoId) });
      qc.invalidateQueries({
        queryKey: drawInitializationKeys.snapshot(rodeoId),
      });
      // Also nuke the broader draw-ops cache — the publish/finalize
      // gate is now reachable, the priority list has shifted, and
      // any cached "is the draw locked" state derived from these
      // should be re-fetched.
      qc.invalidateQueries({ queryKey: ['draw-ops'] });
      qc.invalidateQueries({ queryKey: ['priority-ops'] });
    },
  });
}

/**
 * Fetch the full per-member prior→new snapshot for a rodeo. Used by
 * the audit / history view.
 */
export function useInitializationSnapshot(rodeoId: string | null) {
  return useQuery<DrawInitializationSnapshotDetail>({
    queryKey: drawInitializationKeys.snapshot(rodeoId ?? ''),
    queryFn: () =>
      httpClient.get<DrawInitializationSnapshotDetail>(
        `/draw-ops/rodeo/${rodeoId}/initialization-snapshot`,
      ),
    enabled: !!rodeoId,
    // Snapshot is immutable post-initialization; cache aggressively.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * List initialization snapshots for an association — admins use this
 * to browse "for any rodeo, here's what the users came in as with a
 * priority and here's what they ended up with."
 */
export function useInitializationHistory(
  associationId: string | null,
  limit = 50,
) {
  return useQuery<{
    snapshots: DrawInitializationSnapshotSummary[];
    success: boolean;
  }>({
    queryKey: drawInitializationKeys.history(associationId ?? ''),
    queryFn: () =>
      httpClient.get(
        `/draw-ops/association/${associationId}/initialization-history?limit=${limit}`,
      ),
    enabled: !!associationId,
  });
}
